export type Role<R extends string = string> = {
  name: R;
  description: string;
  composite: boolean;
  implies?: R[];
};

export type Roles<R extends string = string> = { [role: string]: Role<R> };

export type RoleCrudOperation = 'create' | 'read' | 'update' | 'delete' | 'log';

export type CrudImplies<R extends string = string> = Partial<
  Record<RoleCrudOperation, R[]>
>;

const crudOperations: [RoleCrudOperation, string][] = [
  ['create', 'Criação'],
  ['read', 'Visualização'],
  ['update', 'Edição'],
  ['delete', 'Exclusão'],
  ['log', 'Auditoria']
];

const withImplies = <R extends string>(implies?: R[]) =>
  implies?.length ? { implies } : {};

export const roleCreators = <
  R extends string = string,
  C extends string = string
>() => {
  const createRole = (
    name: R,
    description: string,
    implies?: R[]
  ): Roles<R> => ({
    [name]: {
      name,
      description,
      composite: false,
      ...withImplies(implies)
    }
  });

  const createCrudRoles = (
    name: C,
    description: string,
    implies: CrudImplies<R> = {}
  ): Roles<R> =>
    crudOperations.reduce<Roles<R>>((roles, [operation, label]) => {
      const roleName = `${name}_${operation}` as R;
      const operationImplies =
        operation === 'create'
          ? [`${name}_update` as R, ...(implies.create ?? [])]
          : implies[operation];
      return {
        ...roles,
        [roleName]: {
          name: roleName,
          description: `${description} > ${label}`,
          composite: false,
          ...withImplies(operationImplies)
        }
      };
    }, {});

  return { createRole, createCrudRoles };
};

export const { createRole, createCrudRoles } = roleCreators();
